<script setup lang="ts">
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";

import loadingGif from "@Images/Loading.webp";

export type ChartError = {
  title: string;
  message: string;
};

//
// Setup
const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileView = breakpoints.smallerOrEqual("xl");

interface Props {
  isLoading?: boolean;
  isCompact?: boolean;
  isEmpty?: boolean;
  isError?: boolean;
}

withDefaults(defineProps<Props>(), {
  isLoading: false,
  isCompact: false,
  isEmpty: false,
  isError: false,
});

</script>

<template>
  <div
    class="chart-layout"
    :class="{
      'chart-layout--compact': isCompact,
      'chart-layout--mobile': isMobileView,
    }"
  >
    <div class="_chart-layout-header chart-layout__header">
      <slot name="header" />
    </div>
    <div class="chart-layout__content">
      <div
        v-if="isLoading"
        class="_chart-layout-content-loading chart-layout__content-loading"
      >
        <img
          :src="loadingGif"
          alt="Loading"
          class="_chart-layout-loading-image chart-layout__loading-image"
        >
        <p
          v-if="!isCompact"
          class="_chart-layout-loading-message chart-layout__loading-message"
        >
          Loading, please wait...
        </p>
      </div>
      <div
        v-else-if="isEmpty"
        class="_chart-layout-empty-content chart-layout__empty-content"
      >
        <p class="_chart-layout-empty-message chart-layout__empty-message">
          No data found.
        </p>
      </div>
      <div
        v-else-if="isError"
        class="chart-layout__error-content"
      >
        <p class="chart-layout__error-message">
          Something went wrong.
        </p>
      </div>
      <slot v-else />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;

.chart-layout {
  $self: &;

  display: flex;
  flex-flow: column;
  padding: spacing.$xxs;
  overflow: hidden;

  border: border.$primary;
  border-radius: border.$medium-radius;
  background-color: color.$background-secondary;

  &__header {
    margin-bottom: spacing.$xxxs;

    font-size: typography.$h5-font-size;
    line-height: typography.$h5-line-height;
    font-weight: typography.$font-weight-bold;
  }

  &__content {
    flex: 1;
    width: 100%;
    min-width: 0;
  }

  &__loading-image {
    height: 60px;
  }

  &__content-loading,
  &__empty-content,
  &__error-content {
    display: flex;
    flex-flow: column;
    gap: spacing.$xxs;
    align-items: center;
    justify-content: center;
    height: 100%;
  }

  &__loading-message,
  &__empty-message,
  &__error-message {
    margin: 0;

    font-size: typography.$base-font-size;
    line-height: typography.$base-line-height;
    font-weight: typography.$font-weight-medium;
  }

  &--mobile {
    overflow: visible;
  }

  &--compact {
    #{ $self }__header {
      margin: 0;
    }
  }
}
</style>